import type Backbone from 'backbone';
import _ from 'underscore';
import groupProductsByCriterias from '@/js/common/Domain/Service/groupProductsByCriterias.js';
import { OperatorId } from '@entities/operator';
import { operators } from '@fr/constants';
import { type Operator } from '@ve/types';
import { type IBackboneTourProduct } from '@ve/types/backboneModels/Domain/Entity/BackboneTourProduct';
import { type FlightType } from '@ve/types/FlightType';

const SPLIT_BY_FLIGHT_TYPE_OPERATOR_IDS = new Set<number>([OperatorId.AnexTour]);

const FLIGHT_TYPE_CHARTER: FlightType = 1;
const FLIGHT_TYPE_REGULAR: FlightType = 2;

const getCheapestByOperator = (tours: Backbone.Model[], product: Backbone.Model): Backbone.Model[] =>
  _.chain(tours)
    .groupBy((tourData) => (tourData.get('product') || product).get('operator').id)
    .pairs()
    .map(([operatorId, operatorTours]) => {
      const sortedTours = _.sortBy(operatorTours, (operatorTour) => operatorTour.get('finalPrice'));

      if (SPLIT_BY_FLIGHT_TYPE_OPERATOR_IDS.has(Number(operatorId))) {
        const cheapestRegular = sortedTours.find((tour) => tour.get('tour')?.get('flightType') === FLIGHT_TYPE_REGULAR);
        const cheapestCharter = sortedTours.find((tour) => tour.get('tour')?.get('flightType') === FLIGHT_TYPE_CHARTER);
        return [cheapestRegular, cheapestCharter].filter((tour): tour is Backbone.Model => !!tour);
      }

      return [sortedTours[0]];
    })
    .flatten()
    .value();

const prepareTourIdentities = (tours: Backbone.Model[], product: Backbone.Model): Backbone.Model[] =>
  getCheapestByOperator(tours, product);

const prepareTourProductsWithoutHotelAndRailway = (
  tourProductsGroup: Backbone.Model[],
  product: Backbone.Model,
): Backbone.Model[] => {
  const tourProducts = tourProductsGroup.filter((tourData) => tourData.get('type') === 'TourProduct');
  return getCheapestByOperator(tourProducts, product);
};

const getCheapestTravelataTourWithTransfer = (
  tourProductsGroup: IBackboneTourProduct[],
): IBackboneTourProduct | null => {
  const travelataTours = tourProductsGroup.filter((tourProduct) => {
    const operator = tourProduct.get('tour')?.get('operator') as Operator;
    return operators.MY_AGENT.includes(operator?.id);
  });
  const travelataToursWithTransfer = travelataTours.filter((tourProduct) => tourProduct.get('transferType') !== 'none');
  const isTravelataToursWithTransferExists = travelataToursWithTransfer.length > 0;
  return isTravelataToursWithTransferExists
    ? _.sortBy(travelataToursWithTransfer, (tour) => tour.get('price'))[0]
    : null;
};

const getAdditionalTravelataTours = (
  tourProductsGroup: IBackboneTourProduct[],
  cheapestToursIdentities: string[],
): IBackboneTourProduct[] => {
  const filteredTravelataTours = tourProductsGroup.filter((tourProduct) => {
    const operator = tourProduct.get('tour')?.get('operator') as Operator;
    return operators.MY_AGENT.includes(operator?.id);
  });
  const cheapestTravelataTour = filteredTravelataTours.find((t) => cheapestToursIdentities.includes(t.get('id')));
  const sortedTravelataTours = _.sortBy(filteredTravelataTours, (tour) => tour.get('finalPrice'));
  const tourRegular = sortedTravelataTours.find(
    (tour) => tour.get('transferType') === 'none' && !tour.get('tour').get('block'),
  );
  const tourRegularTransfer = sortedTravelataTours.find(
    (tour) => tour.get('transferType') !== 'none' && !tour.get('tour').get('block'),
  );
  const tourBlock = sortedTravelataTours.find(
    (tour) => tour.get('transferType') === 'none' && tour.get('tour').get('block'),
  );
  const tourBlockTransfer = sortedTravelataTours.find(
    (tour) => tour.get('transferType') !== 'none' && tour.get('tour').get('block'),
  );

  return [tourRegular, tourRegularTransfer, tourBlock, tourBlockTransfer].filter(
    (t) => !!t && t.get('id') !== cheapestTravelataTour?.get('id'),
  ) as IBackboneTourProduct[];
};

const getAdditionalToursForBonusMiles = (
  tourProducts: IBackboneTourProduct[],
  allProducts: IBackboneTourProduct[],
): IBackboneTourProduct[] => {
  if (tourProducts.length === 0 || allProducts.length === 0) {
    return [];
  }
  const groupedProducts = groupProductsByCriterias(allProducts);
  const selectedTourId = tourProducts[0]?.get('id');

  const selectedTourGroup = groupedProducts.find((group: Backbone.Model[]) =>
    group.some((tour) => tour.get('id') === selectedTourId),
  );

  if (!selectedTourGroup) {
    return [];
  }
  const existingIds = new Set(tourProducts.map((t) => t.get('id')));
  return selectedTourGroup.filter((tour: Backbone.Model) => !existingIds.has(tour.get('id')));
};

export {
  prepareTourProductsWithoutHotelAndRailway,
  prepareTourIdentities,
  getCheapestTravelataTourWithTransfer,
  getAdditionalTravelataTours,
  getAdditionalToursForBonusMiles,
};
